import {
	useGetAllCommercialDocumentsLazyQuery,
	DocumentStatus,
	DocumentType,
	useDeleteDocumentDraftMutation,
	useFinalizeDocumentMutation,
	useSendDocumentEmailMutation,
	useCancelDocumentMutation,
	useAcceptOfferMutation,
	useRejectOfferMutation,
	useGenerateInvoiceReminderMutation,
} from "@repo/server-api";
import { useNotification } from "@repo/shared/components";
import { useCallback, useState } from "react";

import type { CommercialDocumentSummary } from "../../../../../shared-invoice/src/types/CommercialDocumentTypes";
import type { DataSource } from "@repo/legacy/components/shared/lists/DataSource.js";
import type { PaginationModel } from "@repo/legacy/components/shared/lists/PaginationModel.js";
import type { CommercialDocumentEmailInput, PageInfo } from "@repo/server-api";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { getDocumentStatusesForFilter } from "../../helper/Index";

type CommercialDocumentSummaryFromBackend = {
	id: string;
	number: string;
	grossTotal: number;
	paidTotal: number;
	issueDate: string;
	dueDate: string;
	client: {
		id: string;
		displayName: string;
	};
	contractor: {
		id: string;
		displayName: string;
	};
	clientContactPerson?: {
		id: string;
		displayName: string;
		aditionalInformation?: [
			| {
					emailAddress: string;
			  }
			| object,
		];
	};
	status: DocumentStatus;
	type: DocumentType;
	sentDate?: string | null;
	isInvalid?: boolean;
	isProforma?: boolean;
	project: {
		id: string;
		name: string;
		number: string;
	} | null;
	cancellationReason?: string | null;
	cancellationReversalDocumentId?: string | null;
	rejectionReason?: string | null;
	cancellationReversalDocumentSentDate?: string | null;
	createdDate?: string | null;
	openTotal: number;
};

export function useAllCommercialDocumentsDataSource(): DataSource<CommercialDocumentSummary> {
	const [rows, setRows] = useState<CommercialDocumentSummary[]>([]);
	const [rowCount, setRowCount] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [, setPageInfo] = useState<PageInfo | null>(null);
	const [pageModal, setPageModal] = useState<PaginationModel>({
		page: 0,
		pageSize: 20,
	});
	const [type, setType] = useState<DocumentType[]>([]);
	const [filter, setFilter] = useState({
		startDate: null as string | null,
		endDate: null as string | null,
		searchBy: "",
		isProforma: null as boolean | null,
		sent: null as boolean | null,
		status: [DocumentStatus.Draft] as DocumentStatus[],
		projectId: null as string | null,
	});

	const { showNotification } = useNotification();

	const { t } = useTranslation();

	const _mapInvoiceNodeToSummary = (
		node: CommercialDocumentSummaryFromBackend,
	): CommercialDocumentSummary => {
		return {
			id: node.id,
			number: node.number,
			leftTotal: node.openTotal,
			grossTotal: node.grossTotal,
			issueDate: node.issueDate,
			dueDate: node.dueDate,
			client: {
				id: node.client.id,
				name: node.client.displayName,
			},
			contractor: {
				id: node.contractor.id,
				name: node.contractor.displayName,
			},
			contactPerson:
				node.clientContactPerson &&
				node.clientContactPerson.aditionalInformation &&
				node.clientContactPerson.aditionalInformation.filter(
					(info) => typeof info === "object" && info !== null && "emailAddress" in info,
				).length > 0
					? {
							id: node.clientContactPerson.id,
							name: node.clientContactPerson.displayName,
							email: (node.clientContactPerson.aditionalInformation[0] as { emailAddress: string })
								.emailAddress,
						}
					: null,
			type: node.type,
			status: node.status,
			sentDate: node.sentDate,
			isInvalid: node.isInvalid ?? false,
			isProforma: node.isProforma ?? false,
			project: node.project,
			cancellationReason: node.cancellationReason ?? null,
			stornId: node.cancellationReversalDocumentId,
			rejectionReason: node.rejectionReason ?? null,
			cancellationReversalDocumentSentDate: node.cancellationReversalDocumentSentDate ?? null,
			createdDate: node.createdDate ?? null,
		};
	};

	const [getInvoices] = useGetAllCommercialDocumentsLazyQuery({
		fetchPolicy: "no-cache",
		onError(error: { message: string }) {
			showNotification(error.message, "error");
			setIsLoading(false);
		},
	});

	const [deleteInvoice] = useDeleteDocumentDraftMutation({
		fetchPolicy: "no-cache",
		onError(error: { message: string }) {
			showNotification(error.message, "error");
		},
		onCompleted() {
			showNotification(t("document-deleted-successfully"), "success");
			refresh();
		},
	});

	const [cancelDocument] = useCancelDocumentMutation({
		fetchPolicy: "no-cache",
		onError(error: { message: string }) {
			showNotification(error.message, "error");
		},
		onCompleted(response) {
			if (response.cancelDocument?.errors && response.cancelDocument.errors.length > 0) {
				response.cancelDocument.errors.forEach((error) => {
					showNotification(error.message, "error");
				});
				return;
			}
			showNotification(t("document-cancelled-successfully"), "success");
			refresh();
		},
	});

	const [finalizeInvoice] = useFinalizeDocumentMutation({
		fetchPolicy: "no-cache",
		onError(error: { message: string }) {
			showNotification(error.message, "error");
		},
		onCompleted(data) {
			if (data.finalizeDocument?.errors && data.finalizeDocument.errors.length > 0) {
				data.finalizeDocument.errors.forEach((error) => {
					showNotification(error.message, "error");
				});
				return;
			}
			showNotification(t("document-finalized-successfully"), "success");
			refresh();
		},
	});

	const [sendDocumentEmail] = useSendDocumentEmailMutation({
		fetchPolicy: "no-cache",
		onError(error: { message: string }) {
			showNotification(error.message, "error");
		},
		onCompleted() {
			showNotification(t("document-email-sent-successfully"), "success");
		},
	});

	const [acceptOffer] = useAcceptOfferMutation({
		fetchPolicy: "no-cache",
		onError(error: { message: string }) {
			showNotification(error.message, "error");
		},
		onCompleted(data) {
			if (data.acceptOffer?.errors && data.acceptOffer.errors.length > 0) {
				data.acceptOffer.errors.forEach((error) => {
					showNotification(error.message, "error");
				});
				return;
			}
			showNotification(t("offer-accepted-successfully"), "success");
			refresh();
		},
	});

	const [generateInvoiceReminder] = useGenerateInvoiceReminderMutation({
		fetchPolicy: "no-cache",
		onError(error: { message: string }) {
			showNotification(error.message, "error");
		},
		onCompleted(res) {
			if (res.generateReminder?.errors && res.generateReminder.errors.length > 0) {
				res.generateReminder.errors.forEach((error) => {
					showNotification(error.message, "error");
				});
				return;
			}
			showNotification(t("invoice-reminder-generated-successfully"), "success");
			refresh();
		},
	});

	const [rejectOffer] = useRejectOfferMutation({
		fetchPolicy: "no-cache",
		onError(error: { message: string }) {
			showNotification(error.message, "error");
		},
		onCompleted(data) {
			if (data.rejectOffer?.errors && data.rejectOffer.errors.length > 0) {
				data.rejectOffer.errors.forEach((error) => {
					showNotification(error.message, "error");
				});
				return;
			}
			showNotification(t("offer-rejected-successfully"), "success");
			refresh();
		},
	});

	const fetchDataCommercialDocuments = useCallback(
		(
			page: PaginationModel,
			start: string,
			end: string | null,
			type: DocumentType[],
			s: DocumentStatus[],
			searchBy: string,
			isProforma: boolean | null,
			sent: boolean | null,
			projectId?: string,
		) => {
			setIsLoading(true);
			setType(type);
			setPageModal(page);

			// TODO Hack - fix this
			const url = window.location.href;
			let status: string | string[] = url.split("/");
			status = status[status.length - 1];
			if (status === "unsent") {
				sent = false;
			} else {
				sent = sent ? true : null;
			}

			getInvoices({
				variables: {
					first: page.pageSize,
					after: (page.pageSize * page.page).toString(),
					endDate: end,
					ofStatus: getDocumentStatusesForFilter(status || DocumentStatus.Draft),
					startDate: start,
					ofType: type,
					searchBy: searchBy,
					isProforma: isProforma,
					sent: sent,
					projectId: projectId || null,
				},
			}).then((response) => {
				if (response.data && response.data.allCommercialDocuments?.nodes) {
					let tmp = response.data.allCommercialDocuments.nodes.map((node) =>
						_mapInvoiceNodeToSummary({
							id: node.id ?? "",
							number: node.number ?? "",
							grossTotal: node.grossTotal,
							issueDate: node.issueDate ?? "",
							dueDate: node.dueDate ?? "",
							client: {
								id: node.client?.id || "",
								displayName: node.client?.displayName || "",
							},
							contractor: {
								id: node.contractor?.id || "",
								displayName: node.contractor?.displayName || "",
							},
							clientContactPerson: node.clientContactPerson
								? {
										id: node.clientContactPerson.id,
										displayName: node.clientContactPerson.displayName,
										aditionalInformation:
											node.clientContactPerson.additionalInformation &&
											node.clientContactPerson.additionalInformation.length > 0
												? [node.clientContactPerson.additionalInformation[0]]
												: undefined,
									}
								: undefined,
							project: {
								id: node.project?.id || "",
								name: node.project?.name || "",
								number: node.project?.number || "",
							},
							status: node.status,
							type: node.documentType,
							paidTotal: node.paidTotal || 0,
							sentDate: node.sentDate ?? null,
							isInvalid: node.isInvalid ?? false,
							isProforma: node.isProforma ?? false,
							cancellationReason: node.cancellationReason,
							cancellationReversalDocumentId: node.cancellationReversalDocumentId,
							rejectionReason: node.rejectionReason,
							cancellationReversalDocumentSentDate:
								node.cancellationReversalDocumentSentDate ?? null,
							createdDate: node.createdDate ?? null,
							openTotal: node.openTotal ? node.openTotal : node.grossTotal - (node.paidTotal || 0),
						}),
					);
					if (sent) {
						tmp = tmp.filter((invoice) => invoice.sentDate != null);
					}
					setRows(tmp);
					setRowCount(response.data.allCommercialDocuments.totalCount);
					setPageInfo(response.data.allCommercialDocuments.pageInfo);
					setIsLoading(false);
				}
			});
		},
		[],
	);

	const refresh = useCallback(() => {
		fetchDataCommercialDocuments(
			{
				page: type === type ? pageModal.page : 0,
				pageSize: pageModal.pageSize,
			},
			filter.startDate || dayjs().startOf("year").format("YYYY-MM-DD"),
			filter.endDate,
			type,
			filter.status,
			filter.searchBy,
			filter.isProforma,
			filter.sent,
		);
	}, [filter, pageModal, type, fetchDataCommercialDocuments]);

	const deleteData = useCallback(
		(id: string) => {
			deleteInvoice({ variables: { documentId: id } });
		},
		[deleteInvoice, refresh],
	);

	const cancelData = useCallback(
		(id: string, reason: string) => {
			cancelDocument({ variables: { documentId: id, reason: reason } });
		},
		[cancelDocument, refresh],
	);

	const finalizeData = useCallback(
		(id: string, date: string) => {
			finalizeInvoice({ variables: { documentId: id, issueDate: date } });
		},
		[finalizeInvoice, refresh],
	);
	const sendCommercialDocumentEmail = useCallback(
		(
			type: DocumentType,
			id: string,
			clientId: string,
			emailInfo: {
				body: string;
				recipients: string[];
				ccRecipients: string[];
				bccRecipients: string[];
				shouldMergeAttachments: boolean;
				subject: string;
				attachments?: { name: string; content: string; extension: string }[];
			},
			entityId?: string,
		) => {
			return sendDocumentEmail({
				variables: {
					id: id,
					commercialDocumentEmail: {
						...emailInfo,
					} as CommercialDocumentEmailInput,
					entityId: entityId,
				},
			});
		},
		[sendDocumentEmail],
	);

	const acceptDocument = useCallback(
		(id: string) => {
			acceptOffer({ variables: { id: id } });
		},
		[acceptOffer],
	);

	const rejectDocument = useCallback(
		(id: string, reason: string) => {
			rejectOffer({ variables: { id: id, reason: reason } });
		},
		[rejectOffer],
	);

	const generateReminder = useCallback(
		async (id: string): Promise<any> => {
			return generateInvoiceReminder({ variables: { invoiceId: id } });
		},
		[generateInvoiceReminder],
	);

	return {
		rows,
		rowCount,
		isLoading,
		setIsLoading,
		fetchDataCommercialDocuments,
		refresh,
		deleteData,
		finalizeData,
		sendCommercialDocumentEmail,
		setFilter,
		filter,
		cancelData,
		acceptDocument,
		rejectDocument,
		generateReminder,
	};
}
